import { useEffect, useRef, useState } from 'react';

const AudioMessage = ({
    msg,
    playingAudioId,
    setPlayingAudioId,
    audioProgress,
    setAudioProgress,
    setMessages,
    stopPlaybackSignal,
}) => {
    const audioRef = useRef(null);
    const audioSources = msg.audioUrls?.length ? msg.audioUrls : (msg.audioUrl ? [msg.audioUrl] : []);
    const audioSourcesRef = useRef(audioSources);
    const [currentAudioSrc, setCurrentAudioSrc] = useState(() => audioSources[0] || '');
    const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    useEffect(() => {
        audioSourcesRef.current = audioSources;
    }, [audioSources]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio || stopPlaybackSignal <= 0) return;
        if (!audio.paused) {
            audio.pause();
            audio.currentTime = 0;
            setPlayingAudioId(null);
        }
        setCurrentAudioIndex(0);
        setCurrentAudioSrc(audioSourcesRef.current[0] || '');
        setElapsedSeconds(0);
    }, [stopPlaybackSignal, setPlayingAudioId]);

    useEffect(() => {
        if (currentAudioIndex >= audioSources.length) {
            setCurrentAudioIndex(Math.max(0, audioSources.length - 1));
        }
    }, [audioSources.length, currentAudioIndex]);

    useEffect(() => {
        if (!currentAudioSrc && audioSources[0]) {
            setCurrentAudioSrc(audioSources[0]);
        }
    }, [audioSources, currentAudioSrc]);

    if (msg.isWaitingForAudio && audioSources.length === 0) {
        return (
            <>
                <div className="w-12 h-12 bg-neutral-100 rounded-2xl animate-pulse flex items-center justify-center text-neutral-300">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
                </div>
                <div className="flex-1 flex flex-col gap-2">
                    <div className="h-3 bg-neutral-100 rounded-full w-24 animate-pulse" />
                    <div className="h-1.5 bg-neutral-50 rounded-full w-full animate-pulse" />
                </div>
            </>
        );
    }

    return (
        <>
            <div
                onClick={(e) => {
                    const audio = audioRef.current || e.currentTarget.querySelector('audio');
                    if (audio.paused) {
                        if (currentAudioIndex >= audioSources.length - 1 && audio.ended) {
                            setCurrentAudioIndex(0);
                            setCurrentAudioSrc(audioSourcesRef.current[0] || '');
                            setElapsedSeconds(0);
                        }
                        audio.play();
                    } else {
                        audio.pause();
                    }
                }}
                className={`w-12 h-12 rounded-2xl flex items-center justify-center cursor-pointer hover:scale-105 active:scale-95 transition-all shadow-md relative overflow-hidden ${playingAudioId === msg.id ? 'bg-neutral-800' : 'bg-black'} text-white shrink-0`}
            >
                {playingAudioId === msg.id ? (
                    <svg className="w-5 h-5 z-10" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>
                ) : (
                    <svg className="w-5 h-5 z-10" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                )}
                <audio
                    ref={audioRef}
                    src={currentAudioSrc}
                    autoPlay
                    onPlay={() => {
                        setPlayingAudioId(msg.id);
                    }}
                    onPause={() => {
                        setPlayingAudioId(null);
                    }}
                    onEnded={() => {
                        if (currentAudioIndex < audioSources.length - 1) {
                            const nextIndex = currentAudioIndex + 1;
                            setCurrentAudioIndex(nextIndex);
                            setCurrentAudioSrc(audioSourcesRef.current[nextIndex] || '');
                        } else {
                            setPlayingAudioId(null);
                        }
                    }}
                    onTimeUpdate={(e) => {
                        if (playingAudioId === msg.id) {
                            const previousDuration = (msg.segmentDurations || [])
                                .slice(0, currentAudioIndex)
                                .reduce((total, duration) => total + (Number.isFinite(duration) ? duration : 0), 0);
                            const current = previousDuration + e.target.currentTime;
                            setElapsedSeconds(current);
                            setAudioProgress({ current, duration: e.target.duration });
                        }
                    }}
                    onLoadedMetadata={(e) => {
                        const duration = e.target.duration;
                        if (!Number.isFinite(duration)) return;
                        setMessages(prev => prev.map(m => {
                            if (m.id !== msg.id) return m;
                            const segmentDurations = [...(m.segmentDurations || [])];
                            segmentDurations[currentAudioIndex] = duration;
                            return { ...m, duration, segmentDurations };
                        }));
                    }}
                    className="hidden"
                />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-black uppercase text-[#D81F26] tracking-[0.2em]">Voice Response</span>
                    <span className="text-[10px] font-bold text-black/30 tracking-widest">
                        {elapsedSeconds > 0 ? `${Math.floor(elapsedSeconds / 60)}:${Math.floor(elapsedSeconds % 60).toString().padStart(2, '0')}` : '0:00'}
                    </span>
                </div>
                <div className="relative h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                    <div
                        className="absolute inset-y-0 left-0 bg-[#D81F26] transition-all duration-100 ease-linear"
                        style={{
                            width: playingAudioId === msg.id
                                ? `${(audioProgress.current / audioProgress.duration) * 100}%`
                                : '0%'
                        }}
                    />
                </div>
            </div>
        </>
    );
};

export default AudioMessage;
